import { customElement, resolve } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import template from './maintenance-view.html';
import type {
  ArchiveExportJobStatus,
  BackupCapabilities,
  BackupInspection,
  BackupJobPartStatus,
  BackupJobStatus,
  BackupRestoreResult,
  BackupUserMapping,
  MaintenanceInfo,
  MaintenanceProgressEvent,
} from '../services/api.types';
import {
  downloadBackupPart as downloadBackupPartFile,
  downloadArchiveExport,
  createRestoreResetChallenge,
  getBackupCapabilities,
  getMaintenanceInfo,
  inspectBackup,
  resetForRestore,
  restoreBackup,
  startArchiveExport,
  startBackup,
  streamBackup,
  streamArchiveExport,
  streamMaintenance,
} from '../services/api.service';
import { ConfirmDialog } from '../components/confirm-dialog';

const ARCHIVE_JOB_STORAGE_KEY = 'radish_archive_export_job';
const BACKUP_JOB_STORAGE_KEY = 'radish_backup_job';

@route({ path: 'maintenance', title: 'Maintenance' })
@customElement({ name: 'maintenance-view', template })
export class MaintenanceView {
  private readonly i18n: I18N = resolve(I18N);

  public info: MaintenanceInfo | null = null;
  public loadingInfo = false;
  public infoError = '';

  // File size sync
  public syncRunning = false;
  public syncProgress = 0;
  public syncTotal = 0;
  public syncMessage = '';

  // Folder cleanup
  public foldersRunning = false;
  public foldersProgress = 0;
  public foldersTotal = 0;
  public foldersMessage = '';

  // File hash sync
  public hashRunning = false;
  public hashProgress = 0;
  public hashTotal = 0;
  public hashMessage = '';

  // Page count sync
  public pageCountRunning = false;
  public pageCountProgress = 0;
  public pageCountTotal = 0;
  public pageCountMessage = '';

  // OCR
  public ocrRunning = false;
  public ocrProgress = 0;
  public ocrTotal = 0;
  public ocrMessage = '';

  // Extracted PDF text indexing
  public pdfTextRunning = false;
  public pdfTextProgress = 0;
  public pdfTextTotal = 0;
  public pdfTextMessage = '';

  // Offline archive export
  public archiveExporting = false;
  public archiveMessage = '';
  public archiveError = '';
  public archiveProgress = 0;
  public archiveTotal = 0;
  public archivePhase = '';
  public archiveProgressMessage = '';

  // Logical backup and restore
  public backupCapabilities: BackupCapabilities | null = null;
  public backupFiles: File[] = [];
  public backupStatus: BackupJobStatus | null = null;
  public backupInspection: BackupInspection | null = null;
  public backupMappings: BackupUserMapping[] = [];
  public backupInspecting = false;
  public backupRestoring = false;
  public backupDownloadingPart = 0;
  public backupMessage = '';
  public backupError = '';

  // Restore preparation
  public resetRunning = false;
  public resetMessage = '';
  public resetError = '';

  public get backupPercent(): number {
    if (!this.backupStatus || this.backupStatus.state === 'queued') return 0;
    if (this.backupStatus.state === 'ready') return 100;
    if (this.backupStatus.partCount === 0) return 0;
    return Math.min(99, Math.round(
      (this.backupStatus.processedParts / Math.max(this.backupStatus.partCount, 1)) * 100,
    ));
  }

  public get syncPercent(): number {
    if (this.syncTotal === 0) return 0;
    return Math.round((this.syncProgress / this.syncTotal) * 100);
  }

  public get foldersPercent(): number {
    if (this.foldersTotal === 0) return 100;
    return Math.round((this.foldersProgress / this.foldersTotal) * 100);
  }

  public get hashPercent(): number {
    if (this.hashTotal === 0) return 0;
    return Math.round((this.hashProgress / this.hashTotal) * 100);
  }

  public get pageCountPercent(): number {
    if (this.pageCountTotal === 0) return 0;
    return Math.round((this.pageCountProgress / this.pageCountTotal) * 100);
  }

  public get ocrPercent(): number {
    if (this.ocrTotal === 0) return 0;
    return Math.round((this.ocrProgress / this.ocrTotal) * 100);
  }

  public get pdfTextPercent(): number {
    if (this.pdfTextTotal === 0) return 0;
    return Math.round((this.pdfTextProgress / this.pdfTextTotal) * 100);
  }

  public get archivePercent(): number {
    if (this.archiveTotal === 0) return this.archiveExporting ? 0 : 100;
    return Math.round((this.archiveProgress / this.archiveTotal) * 100);
  }

  public get resetDisabled(): boolean {
    return this.resetRunning ||
      this.syncRunning ||
      this.foldersRunning ||
      this.hashRunning ||
      this.pageCountRunning ||
      this.ocrRunning ||
      this.pdfTextRunning ||
      this.archiveExporting ||
      this.backupInspecting ||
      this.backupRestoring ||
      !!this.backupStatus && ['queued', 'building'].includes(this.backupStatus.state);
  }

  public async loading(): Promise<void> {
    await Promise.all([this.refreshInfo(), this.loadBackupCapabilities()]);
    await this.resumeArchiveExport();
    await this.resumeBackup();
  }

  public formatBackupBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 1024) return `${Math.max(0, Math.round(bytes))} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unit = -1;
    do {
      value /= 1024;
      unit++;
    } while (value >= 1024 && unit < units.length - 1);
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
  }

  public backupFilesChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.backupFiles = input.files ? Array.from(input.files) : [];
    this.backupInspection = null;
    this.backupMappings = [];
    this.backupMessage = '';
    this.backupError = '';
  }

  public async createBackup(): Promise<void> {
    if (this.backupStatus && ['queued', 'building'].includes(this.backupStatus.state)) return;

    this.backupMessage = '';
    this.backupError = '';
    try {
      const started = await startBackup();
      this.backupStatus = started;
      localStorage.setItem(BACKUP_JOB_STORAGE_KEY, started.jobId);
      await this.trackBackup(started.jobId, started);
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
    }
  }

  public async inspectSelectedBackup(): Promise<void> {
    if (this.backupFiles.length === 0 || this.backupInspecting) return;

    this.backupInspecting = true;
    this.backupInspection = null;
    this.backupMappings = [];
    this.backupMessage = '';
    this.backupError = '';
    try {
      this.backupInspection = await inspectBackup(this.backupFiles);
      this.initializeBackupMappings(this.backupInspection);
      this.backupMessage = this.i18n.tr('maintenance.backup.inspectDone');
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
    } finally {
      this.backupInspecting = false;
    }
  }

  public onBackupTargetChanged(mapping: BackupUserMapping, event: Event): void {
    const targetId = Number((event.target as HTMLSelectElement).value);
    const selectedUserId = Number.isFinite(targetId) ? targetId : 0;
    const target = this.backupInspection?.targetUsers.find(user => user.id === selectedUserId);
    const source = this.backupInspection?.users.find(user => user.id === mapping.sourceUserId);
    const updatedMapping: BackupUserMapping = {
      ...mapping,
      targetUserId: selectedUserId,
      targetSub: target?.sub ?? '',
      targetUsername: target?.username ?? source?.username ?? mapping.targetUsername,
      targetEmail: target?.email ?? source?.email ?? mapping.targetEmail,
      targetDisplayName: target?.displayName ?? source?.displayName ?? mapping.targetDisplayName,
    };
    this.backupMappings = this.backupMappings.map(item =>
      item === mapping ? updatedMapping : item);
  }

  public async restoreSelectedBackup(): Promise<void> {
    if (!this.backupInspection || this.backupFiles.length === 0 || this.backupRestoring) return;

    const mapAllToCurrentUser = this.backupInspection.desktopMode;
    if (!mapAllToCurrentUser) {
      const missingSubject = this.backupMappings.find(
        mapping => mapping.targetUserId <= 0 && !mapping.targetSub.trim(),
      );
      if (missingSubject) {
        await ConfirmDialog.alert(
          this.i18n.tr('maintenance.backup.missingSubject'),
          this.i18n.tr('maintenance.backup.mappingTitle'),
        );
        return;
      }

      const duplicateTarget = this.backupMappings
        .filter(mapping => mapping.targetUserId > 0)
        .map(mapping => mapping.targetUserId)
        .find((id, index, values) => values.indexOf(id) !== index);
      if (duplicateTarget !== undefined) {
        await ConfirmDialog.alert(
          this.i18n.tr('maintenance.backup.duplicateTarget'),
          this.i18n.tr('maintenance.backup.mappingTitle'),
        );
        return;
      }
    }

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('maintenance.backup.restoreConfirm', {
        documents: this.backupInspection.documentCount,
        files: this.backupInspection.fileCount,
      }),
      this.i18n.tr('maintenance.backup.restoreTitle'),
    );
    if (!confirmed) return;

    this.backupRestoring = true;
    this.backupMessage = '';
    this.backupError = '';
    try {
      const result = await restoreBackup(
        this.backupFiles,
        this.backupInspection.backupId,
        this.backupMappings.map(mapping => ({
          ...mapping,
          targetUserId: Number(mapping.targetUserId) || 0,
        })),
        mapAllToCurrentUser,
      );
      this.backupMessage = this.formatRestoreResult(result);
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
    } finally {
      this.backupRestoring = false;
    }
  }

  public async resetForRestore(): Promise<void> {
    if (this.resetDisabled) return;

    this.resetRunning = true;
    this.resetMessage = '';
    this.resetError = '';

    try {
      const challenge = await createRestoreResetChallenge();
      const confirmed = await ConfirmDialog.confirmText(
        this.i18n.tr('maintenance.reset.confirm', {
          code: challenge.confirmationCode,
          expires: Math.ceil(challenge.expiresInSeconds / 60),
        }),
        challenge.confirmationCode,
        this.i18n.tr('maintenance.reset.confirmTitle'),
        this.i18n.tr('maintenance.reset.confirmInput'),
        this.i18n.tr('maintenance.reset.confirmPlaceholder'),
        this.i18n.tr('maintenance.reset.confirmMismatch'),
      );
      if (!confirmed) return;

      const result = await resetForRestore(
        challenge.challengeId,
        challenge.confirmationCode,
      );
      this.resetMessage = this.i18n.tr('maintenance.reset.done', {
        documents: result.deletedDocumentCount,
        files: result.deletedFileCount,
        storage: result.deletedStorageEntryCount,
      });

      // The target database changed, so require a fresh inspection/mapping pass.
      this.backupInspection = null;
      this.backupMappings = [];
      this.backupMessage = '';
      this.backupError = '';
      await this.refreshInfo();
    } catch (err) {
      this.resetError = err instanceof Error ? err.message : String(err);
    } finally {
      this.resetRunning = false;
    }
  }

  public async downloadBackupPart(part: BackupJobPartStatus): Promise<void> {
    if (!this.backupStatus || this.backupDownloadingPart !== 0) return;
    this.backupDownloadingPart = part.partNumber;
    this.backupError = '';
    try {
      await downloadBackupPartFile(this.backupStatus.jobId, part.partNumber);
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
    } finally {
      this.backupDownloadingPart = 0;
    }
  }

  public getBackupSourceUser(sourceUserId: number): string {
    const source = this.backupInspection?.users.find(user => user.id === sourceUserId);
    if (!source) return String(sourceUserId);
    return source.displayName || source.username || source.email || source.sub;
  }

  private async loadBackupCapabilities(): Promise<void> {
    try {
      this.backupCapabilities = await getBackupCapabilities();
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
    }
  }

  private async resumeBackup(): Promise<void> {
    const jobId = localStorage.getItem(BACKUP_JOB_STORAGE_KEY);
    if (!jobId || (this.backupStatus && ['queued', 'building'].includes(this.backupStatus.state))) return;

    try {
      await this.trackBackup(jobId);
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
      localStorage.removeItem(BACKUP_JOB_STORAGE_KEY);
    }
  }

  private async trackBackup(jobId: string, initialStatus?: BackupJobStatus): Promise<void> {
    if (initialStatus) this.backupStatus = initialStatus;
    try {
      await streamBackup(jobId, status => {
        this.backupStatus = status;
      });
      this.backupMessage = this.i18n.tr('maintenance.backup.created');
      localStorage.removeItem(BACKUP_JOB_STORAGE_KEY);
    } catch (err) {
      this.backupError = err instanceof Error ? err.message : String(err);
      localStorage.removeItem(BACKUP_JOB_STORAGE_KEY);
      throw err;
    }
  }

  private initializeBackupMappings(inspection: BackupInspection): void {
    const currentUserId = this.backupCapabilities?.currentUserId ?? 0;
    this.backupMappings = inspection.users.map(source => {
      const matchingTarget = inspection.targetUsers.find(target => target.sub === source.sub);
      const targetUserId = inspection.desktopMode
        ? currentUserId
        : (matchingTarget?.id ?? 0);
      return {
        sourceUserId: source.id,
        targetUserId,
        targetSub: matchingTarget?.sub ?? '',
        targetUsername: matchingTarget?.username ?? source.username,
        targetEmail: matchingTarget?.email ?? source.email,
        targetDisplayName: matchingTarget?.displayName ?? source.displayName,
      };
    });
  }

  private formatRestoreResult(result: BackupRestoreResult): string {
    const message = this.i18n.tr('maintenance.backup.restored', {
      users: result.userCount,
      documents: result.documentCount,
      files: result.fileCount,
    });
    return result.warnings.length > 0
      ? `${message} ${result.warnings.join(' ')}`
      : message;
  }

  public async refreshInfo(): Promise<void> {
    this.loadingInfo = true;
    this.infoError = '';
    try {
      this.info = await getMaintenanceInfo();
    } catch (err) {
      this.infoError = err instanceof Error ? err.message : String(err);
    } finally {
      this.loadingInfo = false;
    }
  }

  public async syncFileSizes(onlyMissing: boolean): Promise<void> {
    if (this.syncRunning) return;
    this.syncRunning = true;
    this.syncProgress = 0;
    this.syncTotal = 0;
    this.syncMessage = '';
    let finalMessage = '';

    try {
      await streamMaintenance(
        `/maintenance/sync-file-sizes?onlyMissing=${onlyMissing}`,
        (evt: MaintenanceProgressEvent) => {
          this.syncProgress = evt.processed;
          this.syncTotal = evt.total;
          if (evt.done) {
            this.syncMessage = this.i18n.tr('maintenance.sync.progressDone',
              { updated: evt.updatedCount, skipped: evt.skippedCount, errors: evt.errorCount });
            finalMessage = this.syncMessage;
          } else {
            this.syncMessage = this.i18n.tr('maintenance.progress',
              { processed: evt.processed, total: evt.total });
          }
        },
      );
    } catch (err) {
      finalMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.syncRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.sync.doneTitle'));
    }
    await this.refreshInfo();
  }

  public async deleteEmptyFolders(): Promise<void> {
    if (this.foldersRunning) return;
    if (this.info && this.info.emptyFolderCount === 0) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('maintenance.folders.confirmDelete', { count: this.info?.emptyFolderCount ?? 0 }),
      this.i18n.tr('maintenance.folders.confirmTitle'),
    );
    if (!confirmed) return;

    this.foldersRunning = true;
    this.foldersProgress = 0;
    this.foldersTotal = this.info?.emptyFolderCount ?? 0;
    this.foldersMessage = '';
    let finalMessage = '';

    try {
      await streamMaintenance(
        '/maintenance/delete-empty-folders',
        (evt: MaintenanceProgressEvent) => {
          this.foldersProgress = evt.processed;
          this.foldersTotal = evt.total;
          if (evt.done) {
            this.foldersMessage = this.i18n.tr('maintenance.folders.progressDone',
              { deleted: evt.deletedCount, errors: evt.errorCount });
            finalMessage = this.foldersMessage;
          } else {
            this.foldersMessage = this.i18n.tr('maintenance.progress',
              { processed: evt.processed, total: evt.total });
          }
        },
      );
    } catch (err) {
      finalMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.foldersRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.folders.doneTitle'));
    }
    await this.refreshInfo();
  }

  public async syncFileHashes(onlyMissing: boolean): Promise<void> {
    if (this.hashRunning) return;
    this.hashRunning = true;
    this.hashProgress = 0;
    this.hashTotal = 0;
    this.hashMessage = '';
    let finalMessage = '';

    try {
      await streamMaintenance(
        `/maintenance/sync-file-hashes?onlyMissing=${onlyMissing}`,
        (evt: MaintenanceProgressEvent) => {
          this.hashProgress = evt.processed;
          this.hashTotal = evt.total;
          if (evt.done) {
            this.hashMessage = this.i18n.tr('maintenance.sync.progressDone',
              { updated: evt.updatedCount, skipped: evt.skippedCount, errors: evt.errorCount });
            finalMessage = this.hashMessage;
          } else {
            this.hashMessage = this.i18n.tr('maintenance.progress',
              { processed: evt.processed, total: evt.total });
          }
        },
      );
    } catch (err) {
      finalMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.hashRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.sync.doneTitle'));
    }
    await this.refreshInfo();
  }

  public async syncPageCounts(onlyMissing: boolean): Promise<void> {
    if (this.pageCountRunning) return;
    this.pageCountRunning = true;
    this.pageCountProgress = 0;
    this.pageCountTotal = 0;
    this.pageCountMessage = '';
    let finalMessage = '';

    try {
      await streamMaintenance(
        `/maintenance/sync-page-counts?onlyMissing=${onlyMissing}`,
        (evt: MaintenanceProgressEvent) => {
          this.pageCountProgress = evt.processed;
          this.pageCountTotal = evt.total;
          if (evt.done) {
            this.pageCountMessage = this.i18n.tr('maintenance.sync.progressDone',
              { updated: evt.updatedCount, skipped: evt.skippedCount, errors: evt.errorCount });
            finalMessage = this.pageCountMessage;
          } else {
            this.pageCountMessage = this.i18n.tr('maintenance.progress',
              { processed: evt.processed, total: evt.total });
          }
        },
      );
    } catch (err) {
      finalMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.pageCountRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.sync.doneTitle'));
    }
    await this.refreshInfo();
  }

  public async runOcrMissing(): Promise<void> {
    if (this.ocrRunning) return;
    if (this.info && this.info.pdfFilesWithoutOcr === 0) return;

    this.ocrRunning = true;
    this.ocrProgress = 0;
    this.ocrTotal = this.info?.pdfFilesWithoutOcr ?? 0;
    this.ocrMessage = '';
    let finalMessage = '';

    try {
      await streamMaintenance(
        '/maintenance/run-ocr-missing',
        (evt: MaintenanceProgressEvent) => {
          this.ocrProgress = evt.processed;
          this.ocrTotal = evt.total;
          if (evt.done) {
            this.ocrMessage = this.i18n.tr('maintenance.ocr.progressDone',
              { succeeded: evt.updatedCount, skipped: evt.skippedCount, errors: evt.errorCount });
            finalMessage = this.ocrMessage;
          } else {
            this.ocrMessage = this.i18n.tr('maintenance.progress',
              { processed: evt.processed, total: evt.total });
          }
        },
      );
    } catch (err) {
      finalMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.ocrRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.ocr.doneTitle'));
    }
    await this.refreshInfo();
  }

  public async indexPdfTextMissing(): Promise<void> {
    if (this.pdfTextRunning) return;
    if (this.info && this.info.pdfFilesWithoutText === 0) return;

    this.pdfTextRunning = true;
    this.pdfTextProgress = 0;
    this.pdfTextTotal = this.info?.pdfFilesWithoutText ?? 0;
    this.pdfTextMessage = '';
    let finalMessage = '';

    try {
      await streamMaintenance(
        '/maintenance/index-pdf-text-missing',
        (evt: MaintenanceProgressEvent) => {
          this.pdfTextProgress = evt.processed;
          this.pdfTextTotal = evt.total;
          if (evt.done) {
            this.pdfTextMessage = this.i18n.tr('maintenance.pdfText.progressDone', {
              indexed: evt.updatedCount,
              skipped: evt.skippedCount,
              errors: evt.errorCount,
            });
            finalMessage = this.pdfTextMessage;
          } else {
            this.pdfTextMessage = this.i18n.tr('maintenance.progress', {
              processed: evt.processed,
              total: evt.total,
            });
          }
        },
      );
    } catch (err) {
      finalMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.pdfTextRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.pdfText.doneTitle'));
    }
    await this.refreshInfo();
  }

  public async exportOfflineArchive(): Promise<void> {
    if (this.archiveExporting) return;
    try {
      const started = await startArchiveExport();
      localStorage.setItem(ARCHIVE_JOB_STORAGE_KEY, started.jobId);
      await this.trackArchiveExport(started.jobId, started);
    } catch (err) {
      this.archiveError = err instanceof Error ? err.message : String(err);
    }
  }

  private async resumeArchiveExport(): Promise<void> {
    const jobId = localStorage.getItem(ARCHIVE_JOB_STORAGE_KEY);
    if (!jobId || this.archiveExporting) return;

    try {
      await this.trackArchiveExport(jobId);
    } catch (err) {
      this.archiveError = err instanceof Error ? err.message : String(err);
      localStorage.removeItem(ARCHIVE_JOB_STORAGE_KEY);
    }
  }

  private async trackArchiveExport(jobId: string, initialStatus?: ArchiveExportJobStatus): Promise<void> {
    this.archiveExporting = true;
    this.archiveMessage = '';
    this.archiveError = '';
    this.archiveProgress = 0;
    this.archiveTotal = 0;
    this.archivePhase = '';
    this.archiveProgressMessage = this.i18n.tr('maintenance.archive.queued');

    try {
      if (initialStatus) this.updateArchiveStatus(initialStatus);
      await streamArchiveExport(jobId, status => this.updateArchiveStatus(status));
      await downloadArchiveExport(jobId);
      this.archiveMessage = this.i18n.tr('maintenance.archive.done');
      localStorage.removeItem(ARCHIVE_JOB_STORAGE_KEY);
    } catch (err) {
      this.archiveError = err instanceof Error ? err.message : String(err);
      if (this.archivePhase === 'failed' || /expired|not found/i.test(this.archiveError)) {
        localStorage.removeItem(ARCHIVE_JOB_STORAGE_KEY);
      }
    } finally {
      this.archiveExporting = false;
    }
  }

  private updateArchiveStatus(status: ArchiveExportJobStatus): void {
    this.archiveProgress = status.processed;
    this.archiveTotal = status.total;
    this.archivePhase = status.phase;
    const phaseKey: Record<string, string> = {
      documents: 'maintenance.archive.phase.documents',
      previews: 'maintenance.archive.phase.previews',
      packaging: 'maintenance.archive.phase.packaging',
      ready: 'maintenance.archive.phase.ready',
      failed: 'maintenance.archive.phase.failed',
    };
    const key = phaseKey[status.phase];
    this.archiveProgressMessage = key
      ? this.i18n.tr(key, { processed: status.processed, total: status.total })
      : this.i18n.tr('maintenance.archive.building');
  }
}
